﻿import { faCog, faMap, faRulerHorizontal, faWaterLower, faWaveSine, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { faCog as faCogSolid, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonProps as ButtonPropsBase, Collapse, Divider, Drawer, DrawerProps, FormProps, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import Control from 'ol/control/Control';
import Map from 'ol/Map';
import { GetContainer } from "rc-util/lib/PortalWrapper";
import React, { CSSProperties, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { bathymetryConfig as bathymetryConfigBase } from '../form-configs/BathymetryStateConfig';
import { chartConfig as chartConfigBase } from "../form-configs/ChartStateConfig";
import { chartWmsConfig as chartWmsConfigBase } from "../form-configs/ChartWmsStateConfig";
import { externalLayersStateConfig as externalLayersStateConfigBase } from '../form-configs/ExternalLayersStateConfig';
import { graticuleConfig as graticuleConfigBase } from "../form-configs/GraticuleStateConfig";
import { mapControlsConfig as mapControlsConfigBase } from "../form-configs/MapControlsStateConfig";
import { unitsConfig as unitsConfigBase } from "../form-configs/UnitsStateConfig";
import { ComponentConfigsMap, Properties } from '../forms/ComponentsMap';
import { FormProperties } from '../forms/ObjectForm';
import StateStoreForm from '../forms/StateStoreForm';
import { ChartHybrid, IBathymetryState, IBathymetryStateOptions, IChartState, IChartStateOptions, IChartVisibility, IChartWmsState, IExternalLayersState, isEmptyChart, isMsgChart, isStandAloneChart } from '../models/ChartModels';
import { GraticuleState } from '../models/GraticuleState';
import { MapControlsState } from '../models/MapControlsState';
import { UnitsState } from '../models/UnitsState';
import { BathymetryStore } from '../stores/BathymetryStore';
import { ChartStore } from '../stores/ChartStore';
import { ChartWmsStore } from '../stores/ChartWmsStore';
import { ExternalLayersStore } from '../stores/ExternalLayersStore';
import { GraticuleStore } from '../stores/GraticuleStore';
import { MapControlsStore } from '../stores/MapControlsStore';
import { StateStore } from '../stores/StateStore';
import { UnitsStore } from '../stores/UnitsStore';
import PanelHeader from "./PanelHeader";

import "./map-side-bar.less";

const CLASSNAME = 'map-side-bar';

interface ButtonProps extends Omit<ButtonPropsBase, "onClick" | "id" | "ref"> {
    mobileStyle?: CSSProperties
}

export interface MapSideBarPanel {
    key: string | number,
    title: string,
    icon: ReactElement | IconDefinition,
    hidden?: boolean | (() => boolean),
    /** note: Return a StateStoreForm component to create panel content similar to a default panel (eg chart, units) */
    content: (formProperties: FormProperties) => ReactNode
}

export type MapSideBarCustomStore = {
    store: StateStore<Properties>,
    config: ComponentConfigsMap<Properties, any>
}

export interface MapSideBarCustomStores {
    overlays?: MapSideBarCustomStore
}

interface Props extends Omit<DrawerProps, 'visible' | 'getContainer'> {
    /** Places drawer inside Map target element. */
    map?: Map | undefined,
    /** Places drawer inside this element. */
    htmlDivElement?: HTMLDivElement | null,
    /** Places drawer relative to parent element. */
    useParentElement?: boolean,
    style?: CSSProperties,
    button?: ButtonProps,
    form?: Pick<FormProps, "labelCol" | "wrapperCol">,
    mobile?: boolean,

    /** Force a control to be always visible or hidden. Use 'undefined' to determine visibility by chartConfig. */
    visibilityOverride?: IChartVisibility,

    chart?: ChartStore,
    chartWms?: ChartWmsStore,
    bathymetry?: BathymetryStore,
    graticule?: GraticuleStore,
    mapControls?: MapControlsStore,
    units?: UnitsStore,
    external?: ExternalLayersStore,    

    chartConfig?: ComponentConfigsMap<Omit<IChartState, 'bathymetry'>, IChartStateOptions>,
    chartWmsConfig?: ComponentConfigsMap<IChartWmsState, ChartWmsStore>,
    bathymetryConfig?: (bathymetryStore: BathymetryStore) => ComponentConfigsMap<IBathymetryState, IChartStateOptions>,
    graticuleConfig?: ComponentConfigsMap<GraticuleState>,
    mapControlsConfig?: ComponentConfigsMap<MapControlsState>,
    unitsConfig?: ComponentConfigsMap<UnitsState>,
    externalConfig?: () => ComponentConfigsMap<IExternalLayersState>,

    /** For adding compoments inside new panels */
    customPanels?: MapSideBarPanel[],

    /** For adding components inside existing panels */
    customStores?: MapSideBarCustomStores,
    triggerClose?: number
}

/**
 * Renders a closable Ant design drawer with map settings.
 * All settings are optional.
 * @param param0
 */
const MapSideBar: React.FC<Props> = ({
    map,
    htmlDivElement,
    useParentElement,
    visibilityOverride: visibility,
    onClose,
    button = {},
    form,
    title = "Settings",
    children,
    style,
    bodyStyle,
    mobile,

    chart,
    chartWms,
    bathymetry,
    graticule,
    mapControls,
    units,
    external,

    chartConfig = chartConfigBase,
    chartWmsConfig = chartWmsConfigBase,
    graticuleConfig = graticuleConfigBase,
    mapControlsConfig = mapControlsConfigBase,
    unitsConfig = unitsConfigBase,

    customPanels,
    customStores,
    triggerClose,
    ...rest
}) => {

    const {
        brightness,
        ...chartConfigRest
    } = chartConfig;

    const prefix = "map-options";

    const [visible, setVisible] = useState(false);
    const [buttonId, setButtonId] = useState<string>("");

    const [bathymetryInternal, setBathymetryInternal] = useState<BathymetryStore>();
    const [externalConfig, setExternalConfig] = useState<ComponentConfigsMap<IExternalLayersState>>();
    const [bathymetryConfig, setBathymetryConfig] = useState<ComponentConfigsMap<IBathymetryState, IBathymetryStateOptions>>();

    const buttonRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (external)
            setExternalConfig(externalLayersStateConfigBase(external));
        else
            setExternalConfig(undefined);
    }, [external]);

    useEffect(() => {
        if (bathymetryInternal) {
            setBathymetryConfig(rest.bathymetryConfig ? rest.bathymetryConfig(bathymetryInternal) : bathymetryConfigBase(bathymetryInternal));
        } else
            setBathymetryConfig(undefined);
    }, [bathymetryInternal]);

    useEffect(() => {
        if (bathymetry)
            setBathymetryInternal(bathymetry);
        else if (chart)
            setBathymetryInternal(chart.bathymetry);
    }, [bathymetry, chart]);

    useEffect(() => {
        setButtonId(`${prefix}-${Math.round(10000 * Math.random())}`)
    }, []);

    useEffect(() => {
        if (buttonRef.current && map) {
            map.addControl(new Control({
                element: buttonRef.current,
            }))
        }
    }, [map]);

    useEffect(() => {
        if (triggerClose && triggerClose > 0) {
            close();
        }
    })

    const open = () => setVisible(true);
    const close = () => setVisible(false);

    const properties: FormProperties = {
        form: {
            size: "middle",
            layout: "horizontal",
            hideRequiredMark: true,
            labelCol: form?.labelCol ?? { flex: "0 1 82px" },
            wrapperCol: form?.wrapperCol ?? { flex: "1 1" },
            colon: false
        },
        formItem: {
            style: {
                marginBottom: 4
            }
        }
    }

    const bathymetryVisible = () => {
        if (visibility?.bathymetry !== undefined)
            return visibility.bathymetry;
        else if (chart) {
            const state = chart.getState;
            return state.bathymetry && isMsgChart(state) && state.hybrid === ChartHybrid.Bathymetry;
        }
        return false;
    }

    const chartWmsVisible = () => {
        if (chart) {
            const state = chart.getState;
            return !isEmptyChart(state) && !isStandAloneChart(state);
        }
        return false;
    }

    const renderHeader = (label: ReactNode, icon: IconDefinition | ReactElement) => <PanelHeader label={label} icon={icon} />;

    const renderForm = <P extends Properties, O = void>(
        store: StateStore<P>,
        components: ComponentConfigsMap<P, O>,
        properties?: FormProperties<P>,
        optional?: O
    ) => {
        return <StateStoreForm
            store={store}
            components={components}
            properties={properties}
            optional={optional}
        />
    }

    const getContainer = (): GetContainer | false | undefined => {
        if (htmlDivElement)
            return htmlDivElement;
        else if (useParentElement)
            return false;
        else if (map)
            return map.getTargetElement();
        else
            return undefined;
    }

    const {
        mobileStyle: btnMobileStyle,
        style: btnStyle,
        ...btnRest
    } = button;

    return (
        <>
            <Tooltip title={mobile ? '' : title} placement="left">
                <Button
                    id={buttonId}
                    ref={buttonRef}
                    onClick={open}
                    type="primary" size="middle"
                    icon={<FontAwesomeIcon icon={faCogSolid} fixedWidth />}
                    style={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 0,
                        ...(mobile ? btnMobileStyle : btnStyle)
                    }}
                    {...btnRest}
                />
            </Tooltip>

            <Drawer className={CLASSNAME}
                title={renderHeader(title, faCog)}
                placement="right"
                open={visible}
                width={300}
                mask={false}
                closable={true}
                closeIcon={<FontAwesomeIcon icon={faTimes} fixedWidth />}
                onClose={e => {
                    close();
                    if (onClose)
                        onClose(e);
                }}
                // note: settings getContainer to false places drawer relative to parent element, undefined places drawer in body element
                getContainer={getContainer()}
                style={{
                    position: "absolute",
                    ...style
                }}
                bodyStyle={{
                    padding: 8,
                    ...bodyStyle
                }}
                {...rest}
            >
                <Collapse expandIconPosition='end' defaultActiveKey='chart'>
                    {
                        <Collapse.Panel key='chart' header={renderHeader("Chart", faMap)}>                            
                            <>
                                {
                                    !!chart && renderForm(chart, chartConfigRest, properties, { visibility })
                                }
                                {
                                    !!external &&
                                    !!externalConfig &&
                                    (external.layers.length > 1 || external.enableLayerAdmin) &&
                                    renderForm(external, externalConfig, properties)
                                }
                                {
                                    !!chart && renderForm(chart, { brightness }, properties, { visibility })
                                }
                                {
                                    (!!graticule || !!mapControls || !!customStores?.overlays) && !!chart && <Divider />
                                }
                                {
                                    !!graticule &&
                                    renderForm(graticule, graticuleConfig, properties)
                                }
                                {
                                    !!customStores?.overlays &&
                                    renderForm(customStores.overlays.store, customStores.overlays.config, properties)
                                }
                                {
                                    !!mapControls &&
                                    renderForm(mapControls, mapControlsConfig, properties)
                                }
                            </>                            
                        </Collapse.Panel>
                    }
                    {
                        chartWms && chartWmsVisible() && chartWmsConfig &&
                        <Collapse.Panel key='contours' header={renderHeader("Depth contours", faWaveSine)}>
                            {renderForm(chartWms, chartWmsConfig, properties, chartWms)}
                        </Collapse.Panel>
                    }
                    {
                        bathymetryInternal && bathymetryVisible() && bathymetryConfig &&
                        <Collapse.Panel key='bathymetry' header={renderHeader("Bathymetry", faWaterLower)}>
                            {renderForm(bathymetryInternal, bathymetryConfig, properties, { visibility, type: chart?.type } )}
                        </Collapse.Panel>
                    }
                    {
                        units &&
                        <Collapse.Panel key='units' header={renderHeader("Units", faRulerHorizontal)}>
                            {renderForm(units, unitsConfig, properties)}
                        </Collapse.Panel>
                    }
                    {
                        customPanels && <>
                            {
                                customPanels
                                    .filter(panelIsVisible)
                                    .map(panel => (
                                        <Collapse.Panel key={panel.key} header={renderHeader(panel.title, panel.icon)}>
                                            {panel.content(properties)}
                                        </Collapse.Panel>
                                    ))
                            }
                        </>
                    }
                </Collapse>
                {children}
            </Drawer>
        </>
    )
}

const panelIsVisible = (panel: MapSideBarPanel): boolean => {
    if (!panel.hidden || typeof panel.hidden == 'boolean')
        return !panel.hidden;
    else
        return !panel.hidden();
}

export default observer(MapSideBar);